/**
 * useThread — assembles a full conversation for the selected email.
 *
 * Backend already stamps every inbound (world_inbox) and outbound
 * (world_sent / world_forwarded) message with a stable `threadId`
 * (see functions/helpers/threadingHelper.js). This hook simply fetches every
 * message that shares the selected email's `threadId` across those three
 * collections and merges them into a single chronological list.
 *
 * Notes:
 *   - We query by equality on `threadId` only (no orderBy), so Firestore's
 *     automatic single-field index covers it — no composite index needed.
 *     Ordering happens client-side.
 *   - The selected email is shown instantly (single-message thread) while the
 *     rest of the conversation loads, so there is never a blank pane.
 *   - Legacy mails without a `threadId` simply render as a single message.
 *   - Display only: replying still targets the selected email (handled by the
 *     parent), exactly as before.
 */

import { useState, useEffect, useRef } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';

const MAX_THREAD_MESSAGES = 50;

const THREAD_COLLECTIONS = [
  { name: 'world_inbox', isOutgoing: false },
  { name: 'world_sent', isOutgoing: true },
  { name: 'world_forwarded', isOutgoing: true },
];

// Normalize any timestamp-ish value (ms number, ISO string, Firestore Timestamp)
// into milliseconds for sorting. Unknown → 0 (sorts to the top).
const toMillis = (value) => {
  if (value == null) return 0;
  if (typeof value === 'object' && typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const decorateSelf = (email) => ({
  ...email,
  isOutgoing: email.isSent === true || email.isForwarded === true,
});

export default function useThread(userId, email) {
  const db = getFirestore();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Bumped on every run so a slow fetch can't overwrite a newer selection.
  const reqRef = useRef(0);
  // Latest selected email, read inside the async fetch for the fallback.
  const emailRef = useRef(email);
  emailRef.current = email;

  const emailId = email?.id || null;
  const threadId = email?.threadId || null;

  useEffect(() => {
    if (!userId || !emailId) {
      setMessages([]);
      setLoading(false);
      return undefined;
    }

    const self = decorateSelf(emailRef.current);

    // No thread metadata (legacy mail) → just show the single message.
    if (!threadId) {
      setMessages([self]);
      setLoading(false);
      return undefined;
    }

    // Show the selected mail immediately, then enrich with the rest.
    setMessages([self]);
    setLoading(true);
    const reqId = ++reqRef.current;

    (async () => {
      try {
        const perCollection = await Promise.all(
          THREAD_COLLECTIONS.map(async (col) => {
            const q = query(
              collection(db, col.name, userId, 'emails'),
              where('threadId', '==', threadId),
              limit(MAX_THREAD_MESSAGES)
            );
            const snap = await getDocs(q);
            return snap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              _collection: col.name,
              isOutgoing: col.isOutgoing,
            }));
          })
        );

        if (reqId !== reqRef.current) return; // a newer selection won

        const seen = new Set();
        const merged = [];
        for (const arr of perCollection) {
          for (const msg of arr) {
            const key = `${msg._collection}:${msg.id}`;
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(msg);
          }
        }

        // Guarantee the selected mail is present even if it lacks threadId on
        // its own doc but was opened from the list.
        if (!merged.some((m) => m.id === self.id)) {
          merged.push(self);
        }

        // Newest first — the most recent message (e.g. the reply you just
        // sent) sits at the top of the conversation.
        merged.sort((a, b) => toMillis(b.timestamp || b.date) - toMillis(a.timestamp || a.date));

        setMessages(merged.slice(0, MAX_THREAD_MESSAGES));
      } catch (err) {
        console.warn('[useThread] load failed (showing single message):', err?.message);
        if (reqId === reqRef.current) setMessages([decorateSelf(emailRef.current)]);
      } finally {
        if (reqId === reqRef.current) setLoading(false);
      }
    })();

    return undefined;
  }, [userId, emailId, threadId, db]);

  return { messages, loading };
}
